import { useEffect } from "react";
import { useTranslation } from "../i18n";
import { Globe } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const LANGUAGE_MAP: Record<string, "hr" | "en" | "de"> = {
  HR: "hr", // Croatia
  BA: "hr", // Bosnia and Herzegovina
  RS: "hr", // Serbia
  ME: "hr", // Montenegro
  MK: "hr", // North Macedonia
  SI: "hr", // Slovenia
  DE: "de", // Germany
  AT: "de", // Austria
  CH: "de", // Switzerland
  LI: "de", // Liechtenstein
  // All other countries default to English
};

const LANGUAGE_LABELS = {
  hr: "Hrvatski",
  en: "English",
  de: "Deutsch",
};

export function ShopLanguageSelector() {
  const { language, setLanguage } = useTranslation();

  useEffect(() => {
    // Check if language is already set in localStorage
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      return; // User has already selected a language
    }

    // Attempt geo-location detection
    const detectLanguageByGeoLocation = async () => {
      try {
        // Try to get country code from IP-based geolocation
        const response = await fetch("https://ipapi.co/json/");
        if (!response.ok) throw new Error("Geolocation failed");

        const data = await response.json();
        const countryCode = data.country_code as string;

        // Map country code to language
        const detectedLanguage = LANGUAGE_MAP[countryCode] || "de"; // Default to German

        // Set the detected language
        localStorage.setItem("language", detectedLanguage);
        setLanguage(detectedLanguage);

        console.log(`Language auto-detected: ${detectedLanguage} (Country: ${countryCode})`);
      } catch (error) {
        // Silently fallback to German if detection fails (CORS, network issues, etc.)
        // This is expected behavior and not an error
        localStorage.setItem("language", "de");
        setLanguage("de");
      }
    };

    detectLanguageByGeoLocation();
  }, [setLanguage]);

  const handleLanguageChange = (newLanguage: "hr" | "en" | "de") => {
    setLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  return (
    <div className="flex items-center gap-2">
      <Globe className="h-4 w-4 text-muted-foreground" />
      <Select value={language} onValueChange={handleLanguageChange}>
        <SelectTrigger className="w-[140px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="hr">{LANGUAGE_LABELS.hr}</SelectItem>
          <SelectItem value="en">{LANGUAGE_LABELS.en}</SelectItem>
          <SelectItem value="de">{LANGUAGE_LABELS.de}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
